import React, { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Moon } from 'lucide-react';
import { Toaster } from 'sonner';
import { setTheme } from '../lib/preferences';

const DarkModeContext = createContext({ dark: false, toggle: () => {} });

export const useDarkMode = () => useContext(DarkModeContext);

export const DarkModeProvider = ({ children }) => {
  // [UI-PREFS 2026-10-31] Light unless the toggle chose dark. The #edugate-boot
  // script in index.html applied the saved choice before the first paint;
  // start from what it did. Nothing is written here on load: the old provider
  // followed prefers-color-scheme and saved it on every visit, which left
  // everyone whose phone was dark stuck in dark mode.
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  const toggle = () => {
    const next = !dark;
    setTheme(next ? 'dark' : 'light');
    setDark(next);
  };

  return (
    <DarkModeContext.Provider value={{ dark, toggle }}>
      {children}
    </DarkModeContext.Provider>
  );
};

/**
 * DarkModeToggle — A button that switches between light and dark mode.
 * Usage: <DarkModeToggle />
 */
export const DarkModeToggle = ({ className = '' }) => {
  const { dark, toggle } = useDarkMode();
  const { t } = useTranslation();
  const label = dark ? t('switch_to_light') : t('switch_to_dark');
  return (
    <button
      type="button"
      onClick={toggle}
      title={label}
      aria-label={label}
      aria-pressed={dark}
      className={`relative p-2.5 rounded-xl transition-all hover:scale-110 min-h-[44px] min-w-[44px] flex items-center justify-center ${dark ? 'bg-amber-400 text-black' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'} ${className}`}
    >
      <div className="relative w-5 h-5">
        <Sun  className={`w-5 h-5 absolute inset-0 transition-all duration-300 ${dark ? 'opacity-100 rotate-0' : 'opacity-0 rotate-90'}`} />
        <Moon className={`w-5 h-5 absolute inset-0 transition-all duration-300 ${dark ? 'opacity-0 -rotate-90' : 'opacity-100 rotate-0'}`} />
      </div>
    </button>
  );
};

/**
 * ThemedToaster — sonner's <Toaster> in the current theme. [UI-PREFS 2026-10-31]
 * Without `theme` sonner draws every toast light, a white box over the dark UI.
 */
export const ThemedToaster = (props) => {
  const { dark } = useDarkMode();
  return <Toaster theme={dark ? 'dark' : 'light'} {...props} />;
};
